import { useCallback, useEffect, useState } from 'react';
import { userPreferencesApi } from '../services/api';
import type { ProjectsListSort } from '../components/ProjectsListTable';

/**
 * The sort a list was last left with, saved via `userPreferencesApi` under
 * `preferenceKey` like the column order (list keys stay in this browser).
 * Uses `defaultSort` until the saved choice loads, and when the saved field
 * no longer exists.
 */
export function useListSort(
  preferenceKey: string,
  defaultSort: ProjectsListSort,
  fields: string[],
): [ProjectsListSort, (sort: ProjectsListSort) => void] {
  const [sort, setSort] = useState<ProjectsListSort>(defaultSort);

  useEffect(() => {
    userPreferencesApi.getPreferences().then((prefs) => {
      const saved = prefs[preferenceKey] as Partial<ProjectsListSort> | undefined;
      if (saved && typeof saved.field === 'string' && fields.includes(saved.field)
        && (saved.direction === 'asc' || saved.direction === 'desc')) {
        setSort({ field: saved.field, direction: saved.direction });
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferenceKey]);

  const changeSort = useCallback((next: ProjectsListSort) => {
    setSort(next);
    userPreferencesApi.savePreference(preferenceKey, next).catch((err) => {
      console.error('Failed to save list sort:', err);
    });
  }, [preferenceKey]);

  return [sort, changeSort];
}
